import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { DialogContent, Dialog } from "@volca/ui/components/ui/dialog";
import { InfoWithLabel } from "@volca/ui/components/ui/info-with-label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { useBusiness } from "@/core/business";
import { formatDate } from "@/core/utils/date";
import { REVIEW_PLATFORM_TO_DISPLAY } from "@/reviews/constants";
import { useGetReview } from "@/reviews/query";
import { normalizeRating } from "@/reviews/utils";
import { ReviewAttributionContent } from "./review-attribution-content";
import { ReviewsSidebarSkeleton } from "./reviews-sidebar-skeleton";
import FacebookLogoSrc from "@/assets/images/facebook-logo.png";
import GoogleLogoSrc from "@/assets/images/google-logo.png";
import TripAdvisorLogoSrc from "@/assets/images/trip-advisor-logo.jpeg";
import YelpLogoSrc from "@/assets/images/yelp-icon.png";

const PLATFORM_TO_ICON: Record<string, string> = {
  google: GoogleLogoSrc,
  facebook: FacebookLogoSrc,
  yelp: YelpLogoSrc,
  tripadvisor: TripAdvisorLogoSrc,
};

interface ReviewsSidebarProps {
  reviewId: string;
  onClose: () => void;
}

export const ReviewsSidebar = ({ reviewId, onClose }: ReviewsSidebarProps) => {
  const { businessId } = useBusiness();
  const { data: review, isLoading } = useGetReview(businessId, reviewId);

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (isLoading) {
    return <ReviewsSidebarSkeleton />;
  }

  if (!review) {
    return (
      <BusinessSettingsSidebarWrapper>
        <div className="p-5">Review not found.</div>
      </BusinessSettingsSidebarWrapper>
    );
  }

  const platformIconSrc = review.platform
    ? PLATFORM_TO_ICON[review.platform]
    : undefined;

  const rating = normalizeRating(review.rating);

  const dateStr = review.external_created_at || review.created_at;

  const getAttributionText = () => {
    const { technicians } = review;
    if (!technicians || technicians.length === 0) return null;

    return technicians
      .map((t) => t.display_identification)
      .filter(Boolean)
      .join(", ");
  };

  const attributionText = getAttributionText();

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText="Review"
        name={review.reviewer_name}
        onClose={onClose}
      />
      <Stack align="start" gap={4} className="px-6 pt-4">
        <Button
          onClick={() => setIsModalOpen(true)}
          className="w-full"
          size="sm"
        >
          Attribute
        </Button>
      </Stack>
      <Stack
        align="start"
        gap={4}
        className="w-full flex-1 overflow-auto px-6 py-4"
      >
        <Stack className="w-full" align="start" gap={4}>
          <InfoWithLabel label="Date">{formatDate(dateStr)}</InfoWithLabel>

          {review.platform && (
            <InfoWithLabel label="Platform">
              <Stack direction="row" align="center" gap={2}>
                {platformIconSrc && (
                  <img
                    src={platformIconSrc}
                    alt={`${review.platform || "Google"} logo`}
                    className="h-4 w-4 object-contain"
                  />
                )}
                <Text>
                  {REVIEW_PLATFORM_TO_DISPLAY[
                    review.platform as keyof typeof REVIEW_PLATFORM_TO_DISPLAY
                  ] || review.platform}
                </Text>
              </Stack>
            </InfoWithLabel>
          )}

          <InfoWithLabel label="Rating">
            <Stack direction="row" gap={1} align="center">
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon
                  weight="fill"
                  key={`star-icon-${i}`}
                  className={cn("h-4 w-4 text-warning", {
                    "opacity-30": i >= rating,
                  })}
                />
              ))}
            </Stack>
          </InfoWithLabel>

          {review.body && (
            <InfoWithLabel label="Review">
              <Text tone="muted">{review.body}</Text>
            </InfoWithLabel>
          )}

          {attributionText && (
            <InfoWithLabel label="Attribution">
              <Text tone="muted">{attributionText}</Text>
            </InfoWithLabel>
          )}
        </Stack>
      </Stack>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <ReviewAttributionContent
            reviewId={review.id}
            onClose={() => setIsModalOpen(false)}
          ></ReviewAttributionContent>
        </DialogContent>
      </Dialog>
    </BusinessSettingsSidebarWrapper>
  );
};
